import React, { useState } from 'react';
import {
  BooleanField,
  Datagrid,
  FunctionField,
  NumberField,
  ReferenceManyField,
  TextField,
  useGetRecordId,
  useRecordContext,
  useResourceContext,
} from 'react-admin';
import { Button } from '@mui/material';
import { DisplayHelpers } from 'utils/helpers';
import { Link } from 'react-router-dom';
import LinkField from 'components/LinkField';
import MoneyField from 'components/MoneyField';
import { SettingsNestedOperatorSelect } from 'components/SettingsNestedOperatorSelect';
import { getRateSettingsHash, getRateSettingsInfo } from './PositionRateSettingsTab.utils';
import { Check, Clear } from '@mui/icons-material';

const RateSettingsTab = () => {
  const record = useRecordContext();
  const id = useGetRecordId();
  const resource = useResourceContext();

  const [settingsNestedOperator, setSettingsNestedOperator] = useState(() => {
    if (resource !== 'Location') {
      return 'ten99';
    }

    return record.primary_billing_group?.plan === 'enterprise_w2' ? 'w2.local' : 'ten99';
  });

  const isW2 = settingsNestedOperator.includes('w2');
  const isLocation = resource === 'Location';
  const isW2Location = isLocation && isW2;
  const rateSettingName = `${resource}RateSetting`;
  const target = `${resource.toLowerCase()}_id`;
  const primaryBillingGroupPlan = record?.primary_billing_group?.plan;
  const source = JSON.stringify({ [target]: id, bgPlan: primaryBillingGroupPlan });
  const addRateSettingsUrl = `/${rateSettingName}/create?source=${source}`;
  const filter = resource === 'State' ? {} : { include_parents: true };
  const ratesPrefix = isW2 ? 'W2' : '1099';
  const recordLocation = { primary_billing_group: record?.primary_billing_group ?? {} };

  return (
    <>
      <Button component={Link} color="primary" to={addRateSettingsUrl}>
        Add rate settings
      </Button>
      <ReferenceManyField reference={rateSettingName} filter={filter} target={target}>
        <SettingsNestedOperatorSelect
          setSettingsNestedOperator={setSettingsNestedOperator}
          settingsNestedOperator={settingsNestedOperator}
        />
        <>
          <Datagrid key={settingsNestedOperator} bulkActionButtons={false}>
            <LinkField source="id" />
            <BooleanField source="active" />
            {isLocation && (
              <BooleanField label="Track Unpaid Breaks" source="settings.track_unpaid_breaks" />
            )}
            <TextField source="settings.burden_currency" label="Burden Currency" />
            <NumberField source="settings.vms_fee_percent" label="VMS Fee Percent" />
            <NumberField source="settings.agency_vms_fee_percent" label="Agency VMS Fee Percent" />
            <TextField source="settings.cancellation_fee_window" label="Cancellation Fee Window" />
            <TextField source="settings.other_burden_multiplier" label="Other Burden Multiplier" />
            {isLocation && (
              <FunctionField
                label={`${ratesPrefix} Invoicing Markup`}
                render={({ settings }) => {
                  const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                  return rateSettingsHash?.invoicing_markup ?? 0;
                }}
              />
            )}
            {isW2Location && (
              <FunctionField
                label={`${ratesPrefix} Operational Markup`}
                render={({ settings }) => {
                  const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                  return rateSettingsHash?.operational_markup ?? 0;
                }}
              />
            )}
            {isLocation && (
              <FunctionField
                label={`${ratesPrefix} Misc Markup`}
                render={({ settings }) => {
                  const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                  return rateSettingsHash?.misc_markup ?? 0;
                }}
              />
            )}
            <TextField
              source="settings.guaranteed_minimum_hours"
              label="Guaranteed Minimum Hours"
            />
            <TextField
              source="settings.w2.billable_orientation_hours"
              label="W2 Billable Orientation Hours"
            />
            {isLocation && (
              <TextField
                source="settings.w2.non_billable_orientation_burden_hours"
                label="W2 Non Billable Orientation Burden Hours"
              />
            )}
            <FunctionField
              label={`${ratesPrefix} Charge Extended Rate`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  rateSettingsHash?.[rateSettingsPrefix]?.charge_overtime_multiplier,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Charge Second Extended Rate`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  rateSettingsHash?.[rateSettingsPrefix]?.charge_double_overtime_multiplier,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Charge Extended Rate Starts At`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  rateSettingsHash?.[rateSettingsPrefix]?.charge_max_daily_regular_hours,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Charge Second Extended Rate Starts At`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  (rateSettingsHash?.[rateSettingsPrefix]?.charge_max_daily_regular_hours || 0) +
                    (rateSettingsHash?.[rateSettingsPrefix]?.charge_max_daily_overtime_hours || 0),
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Charge Weekly Extended Rate Starts At`}
              render={({ settings }) => {
                return DisplayHelpers.roundToTwoDecimals(
                  getRateSettingsHash(settings, settingsNestedOperator)
                    ?.charge_max_weekly_regular_hours,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Charge Seventh Day Rule Enabled`}
              render={({ settings }) => {
                return getRateSettingsHash(settings, settingsNestedOperator)
                  ?.charge_seventh_day_rule_enabled ? (
                  <Check />
                ) : (
                  <Clear />
                );
              }}
            />

            <TextField
              source="settings.w2.medicare_burden_multiplier"
              label="W2 Medicare Burden Multiplier"
            />

            <FunctionField
              label={`${ratesPrefix} Payout Overtime Multiplier`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  rateSettingsHash?.[rateSettingsPrefix]?.payout_overtime_multiplier,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Payout Second Extended Rate Multiplier`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  rateSettingsHash?.[rateSettingsPrefix]?.payout_double_overtime_multiplier,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Payout Extended Rate Starts At`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  rateSettingsHash?.[rateSettingsPrefix]?.payout_max_daily_regular_hours,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Payout Second Extended Rate Starts At`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  (rateSettingsHash?.[rateSettingsPrefix]?.payout_max_daily_regular_hours || 0) +
                    (rateSettingsHash?.[rateSettingsPrefix]?.payout_max_daily_overtime_hours || 0),
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Payout Weekly Extended Rate Starts At`}
              render={({ settings }) => {
                const rateSettingsPrefix = getRateSettingsInfo(
                  settings,
                  settingsNestedOperator,
                  recordLocation,
                ).rateSettingsPrefix;
                const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
                return DisplayHelpers.roundToTwoDecimals(
                  rateSettingsHash?.[rateSettingsPrefix]?.payout_max_weekly_regular_hours,
                );
              }}
            />

            <FunctionField
              label={`${ratesPrefix} Payout Seventh Day Rule Enabled`}
              render={({ settings }) => {
                return getRateSettingsHash(settings, settingsNestedOperator)
                  ?.payout_seventh_day_rule_enabled ? (
                  <Check />
                ) : (
                  <Clear />
                );
              }}
            />

            <TextField
              source="settings.cancellation_fee_charge_hours"
              label="Cancellation Fee Charge Hours"
            />
            <TextField
              source="settings.w2.social_security_burden_multiplier"
              label="W2 Social Security Burden Multiplier"
            />
            <TextField
              source="settings.w2.state_unemployment_percent_burden_multiplier"
              label="W2 State Unemployment Percent Burden Multiplier"
            />
            <TextField
              source="settings.w2.federal_unemployment_percent_burden_multiplier"
              label="W2 Federal Unemployment Percent Burden Multiplier"
            />
            <MoneyField
              source="settings.w2.state_unemployment_max_burden_cents"
              label="W2 State Unemployment Max Burden"
            />
            <MoneyField
              source="settings.w2.federal_unemployment_max_burden_cents"
              label="W2 Federal Unemployment Max Burden"
            />
            <MoneyField label="W2 ADP fee" source="settings.w2.adp_fee_burden_cents" />
            <MoneyField label="W2 Medical (Per Week)" source="settings.w2.medical_burden_cents" />
            <MoneyField source="settings.w2.compliance_burden_cents" label="W2 Compliance Burden" />
            <MoneyField source="settings.rn_minimum_wage_cents" label="RN Minimum Wage" />
            <MoneyField source="settings.lvn_minimum_wage_cents" label="LVN Minimum Wage" />
            <MoneyField source="settings.allied_minimum_wage_cents" label="Allied Minimum Wage" />
            {resource === 'State' && (
              <MoneyField source="settings.w2_minimum_wage_cents" label="W2 Minimum Wage" />
            )}
          </Datagrid>
        </>
      </ReferenceManyField>
    </>
  );
};

export default RateSettingsTab;
